import { Subscription } from "../../api/subscriptionApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { AlertCircle, Clock, CreditCard, Euro, Repeat } from "lucide-react";
import { weekdays } from "../Weekdays.ts";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react/macro";
import { Link } from "react-router";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";

interface UserPendingSubscriptionCardProps {
  subscription: Subscription;
}

export default function UserPendingSubscriptionCard({
  subscription,
}: UserPendingSubscriptionCardProps) {
  const { language } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { i18n } = useLingui();

  const InstrumentIcon = getInstrumentIcon(subscription.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    subscription.instrument,
    language,
  );

  const weekday = weekdays.find((w) => w.value === subscription.dayOfWeek);
  const weekdayLabel = weekday ? i18n._(weekday.label) : subscription.dayOfWeek;

  const fromDisplay = subscription.from.substring(0, 5);
  const toDisplay = subscription.to.substring(0, 5);

  const isAccepted = subscription.status === "ACCEPTED";
  const borderColor = isAccepted ? "border-blue-200" : "border-amber-200";
  const bgColor = isAccepted ? "bg-blue-50" : "bg-amber-50";
  const textColor = isAccepted ? "text-blue-900" : "text-amber-900";
  const iconColor = isAccepted ? "text-blue-700" : "text-amber-700";
  const metaColor = isAccepted ? "text-blue-900" : "text-amber-900";

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border ${borderColor} ${bgColor} p-4 shadow-[--shadow-card]`}
    >
      {/* Header: instrument + status badge */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <InstrumentIcon className={`h-5 w-5 shrink-0 ${iconColor}`} />
          <span className={`truncate text-sm font-semibold ${textColor}`}>
            {instrumentLabel}
          </span>
        </div>
        {isAccepted ? (
          <div className="flex shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
            <CreditCard className="h-3 w-3" />
            <Trans>Awaiting Payment</Trans>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            <AlertCircle className="h-3 w-3" />
            <Trans>Pending Acceptance</Trans>
          </div>
        )}
      </div>

      {/* Teacher info */}
      {subscription.teacherName && (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0">
            <ProfilePicture
              profilePictureUrl={subscription.teacherProfilePictureUrl}
              className="h-9 w-9"
              iconSize={20}
            />
          </div>
          <Link
            to={`/profile/${subscription.teacherId}`}
            className={`truncate text-sm font-medium ${textColor} hover:underline`}
          >
            {subscription.teacherName}
          </Link>
        </div>
      )}

      {/* Day + time range */}
      <div
        className={`flex items-center gap-1.5 text-xs font-medium ${metaColor}`}
      >
        <Clock className="h-3.5 w-3.5 shrink-0 opacity-60" />
        <span>
          {weekdayLabel}, {fromDisplay} – {toDisplay}
        </span>
      </div>

      {/* Monthly price */}
      {subscription.price !== undefined && (
        <div
          className={`flex items-center gap-1.5 text-xs font-medium ${metaColor}`}
        >
          <Euro className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span>
            {subscription.price.toFixed(2)} / <Trans>month</Trans>
          </span>
        </div>
      )}

      {/* Pay action — only when teacher accepted and awaiting payment */}
      {isAccepted && (
        <Link
          to={`/subscriptions/${subscription.id}/checkout`}
          className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-[0.98]"
        >
          <Repeat className="h-4 w-4" />
          <span>
            <Trans>Complete Payment</Trans>
          </span>
        </Link>
      )}
    </div>
  );
}
